"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { useModal } from "@/components/modals/ModalProvider";
import { getAuthedContext } from "@/lib/auth/getAuthedContext";
import {
  MODERATION_REASONS,
  MODERATION_REASON_LABELS,
} from "@/lib/moderation/reasons";

const MAX_DETAILS = 1000;
const DIALOG_FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function ReportModal({
  open,
  onClose,
  targetType,
  targetId,
  targetLabel,
  meta,
  onSubmitted,
  // Optional stable element to return focus to when the control that opened the modal unmounts
  // (e.g. a menu item inside a menu that closes as the modal opens).
  returnFocusRef = null,
  zIndexClassName = "z-[80]",
}) {
  const { user } = useAuth();
  const { openModal } = useModal();
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);
  const reasonRef = useRef(null);
  const idPrefix = useId();
  const titleId = `${idPrefix}-title`;
  const targetLabelId = `${idPrefix}-target`;
  const reasonId = `${idPrefix}-reason`;
  const detailsId = `${idPrefix}-details`;
  const detailsCountId = `${idPrefix}-details-count`;

  const reasons = useMemo(() => {
    if (meta?.content_type === "story") return MODERATION_REASONS.listing || [];
    const key = String(targetType || "").toLowerCase();
    return MODERATION_REASONS[key] || [];
  }, [meta?.content_type, targetType]);

  // Move focus into the dialog on open; on close, hand it back to the control that opened it
  // (or the returnFocusRef fallback) unless something else (e.g. the login modal) has taken focus.
  useEffect(() => {
    if (!open) return undefined;
    const active = document.activeElement;
    const opener = active && active !== document.body ? active : null;
    const fallback = returnFocusRef?.current || null;
    const dialog = dialogRef.current;
    reasonRef.current?.focus();
    return () => {
      const current = document.activeElement;
      const focusLost = !current || current === document.body || dialog?.contains(current);
      if (!focusLost) return;
      const target = opener?.isConnected ? opener : fallback?.isConnected ? fallback : null;
      target?.focus?.();
    };
  }, [open, returnFocusRef]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        // Same rule as the Close and Cancel buttons, which stay available while submitting.
        onClose?.();
        return;
      }
      if (event.key !== "Tab") return;
      // aria-modal: keep Tab / Shift+Tab cycling through the dialog's own enabled controls.
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(DIALOG_FOCUSABLE)).filter(
        (element) => !element.closest("[hidden]")
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // Focus outside the dialog, or on a control that has since been disabled (Submit while
      // submitting), is stale: bring it back to the ends of the current focusable set.
      const activeIndex = focusable.indexOf(document.activeElement);
      if (event.shiftKey && activeIndex <= 0) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (activeIndex === -1 || activeIndex === focusable.length - 1)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const title =
    meta?.content_type === "story"
      ? "Report this Story"
      : String(targetType || "").toLowerCase() === "review"
      ? "Report this review"
      : String(targetType || "").toLowerCase() === "listing"
        ? "Report this listing"
        : "Report this account";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    if (!user?.id) {
      onClose?.();
      openModal("customer-login");
      return;
    }

    if (!targetType || !targetId || !reason) {
      setError("Select a reason before submitting.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const { client } = await getAuthedContext("createModerationFlag");
      const payloadMeta = {
        ...(meta || {}),
        page_url:
          typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search}`
            : "",
      };

      const isStoryReport = meta?.content_type === "story" && meta?.story_id;
      const { error: rpcError } = isStoryReport
        ? await client.rpc("create_story_moderation_flag", {
            p_story_id: meta.story_id,
            p_reason: reason,
            p_details: details.trim() || null,
          })
        : await client.rpc("create_moderation_flag", {
            p_target_type: targetType,
            p_target_id: targetId,
            p_reason: reason,
            p_details: details.trim() || null,
            p_meta: payloadMeta,
          });

      if (rpcError) {
        const message = String(rpcError.message || "");
        if (/Not authenticated/i.test(message)) {
          onClose?.();
          openModal("customer-login");
          return;
        }
        throw rpcError;
      }

      setReason("");
      setDetails("");
      onSubmitted?.({
        type: "success",
        message: "Thanks - your report has been received.",
      });
      onClose?.();
    } catch (err) {
      const message = String(err?.message || "Could not submit your report.");
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center bg-white/80 px-4 py-4 backdrop-blur-sm`}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={targetLabel ? targetLabelId : undefined}
        className="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto overscroll-contain rounded-2xl border border-gray-300 bg-white p-6 text-gray-900 shadow-xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 id={titleId} className="text-lg font-bold text-gray-900">{title}</h3>
            {targetLabel ? (
              <p id={targetLabelId} className="mt-1 text-sm text-gray-700">{targetLabel}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            aria-label="Close report modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label htmlFor={reasonId} className="mb-1 block text-sm font-medium text-gray-800">
              Reason
            </label>
            <select
              ref={reasonRef}
              id={reasonId}
              required
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="">Select a reason</option>
              {reasons.map((code) => (
                <option key={code} value={code}>
                  {MODERATION_REASON_LABELS[code] || code}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={detailsId} className="mb-1 block text-sm font-medium text-gray-800">
              Additional details (optional)
            </label>
            <textarea
              id={detailsId}
              aria-describedby={detailsCountId}
              value={details}
              onChange={(event) => setDetails(event.target.value.slice(0, MAX_DETAILS))}
              rows={4}
              maxLength={MAX_DETAILS}
              placeholder="Provide context to help our moderation team."
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
            <p id={detailsCountId} className="mt-1 text-xs text-gray-600">{details.length}/{MAX_DETAILS}</p>
          </div>

          {error ? (
            <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !reason}
              className="yb-primary-button rounded-lg px-4 py-2 text-sm font-semibold text-white"
            >
              {submitting ? "Submitting..." : "Submit report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
